// Parcel crosswalks from an HD cortex pack's per-vertex label layers (asset-pack v0.3). Two label
// layers on the same vertices, such as `schaefer400` and `dkt`, say how many vertices of each
// destination parcel each source parcel covers. Values a sim keys by the source atlas (pharma's
// Schaefer-400 parcels) then become values per destination structure (v0's DKT parcels), which
// viewport API 0.2 colours with `overlay`: each region takes the mean over its vertices.

import { decode, parseDType } from './binary.ts';
import { SimFiles, type SimSource } from './files.ts';

/** The pack the viewport draws as the MNI152 2009c template's HD cortex (the embed's own rule). */
export const HD_CORTEX = 'mni152-2009c-cortex-hd';

export interface Crosswalk {
  pack: { id: string; version: string };
  /** Source and destination label layer names. */
  from: string;
  to: string;
  /** Destination structure ids, in label order, left hemisphere first. */
  keys: string[];
  /** Per key: the source labels on its vertices, and how many vertices each covers. */
  parts: { label: number; vertices: number }[][];
  /** Vertices that carry both labels. */
  vertices: number;
}

interface LayerEntry {
  name: string;
  kind: string;
  dtype: string;
  file: string;
  nodata?: number | string;
  labels?: Record<string, { structure?: string }>;
}

/** packs/ through the sims readers. The server lists sims/ directories, not packs/ ones. */
export const packSource = (): SimSource => ({ base: '/packs/', fetch: (...a) => fetch(...a), listing: false });

const made = new Map<string, Promise<Crosswalk | null>>();

/** Forget computed crosswalks (tests). */
export function clearCrosswalks() {
  made.clear();
}

/**
 * The crosswalk from label layer `from` to label layer `to` (whose labels name structures) on every
 * surface of pack `id`. Null when the pack isn't published or a surface lacks either layer, so a
 * consumer switches on when the layers arrive, whatever the pack's version.
 */
export function crosswalk(id: string, from: string, to: string, src: SimSource = packSource()): Promise<Crosswalk | null> {
  const key = `${src.base}${id}:${from}:${to}`;
  let p = made.get(key);
  if (!p) {
    p = build(new SimFiles(src, id), from, to);
    made.set(key, p);
    p.catch(() => made.delete(key));
  }
  return p;
}

async function build(pack: SimFiles, from: string, to: string): Promise<Crosswalk | null> {
  const [manifest, structures] = await Promise.all([pack.maybeJson<{ id?: string; version?: string }>('pack.json'), pack.maybeJson<{ layers?: unknown }[]>('structures.json')]);
  if (!manifest || !Array.isArray(structures)) return null;
  const surfaces = structures.map((s) => s.layers).filter((l): l is string => typeof l === 'string');
  if (!surfaces.length) return null;
  const counts = new Map<string, Map<number, number>>();
  let vertices = 0;
  for (const index of surfaces) {
    const ix = await pack.maybeJson<{ vertices?: number; layers?: LayerEntry[] }>(index);
    const layer = (name: string) => ix?.layers?.find((l) => l.name === name && l.kind === 'label');
    const src = layer(from);
    const dst = layer(to);
    const n = ix?.vertices ?? 0;
    if (!src || !dst || !n) return null;
    const read = async (l: LayerEntry) => {
      const v = decode(await pack.bytes(l.file), parseDType(l.dtype));
      if (v.length !== n) throw new Error(`${l.file}: ${v.length} labels for ${n} vertices`);
      return v;
    };
    const [sv, dv] = await Promise.all([read(src), read(dst)]);
    const srcNodata = Number(src.nodata ?? 0);
    const dstNodata = Number(dst.nodata ?? 0);
    const structure = new Map<number, string>();
    for (const [value, entry] of Object.entries(dst.labels ?? {})) if (entry.structure) structure.set(Number(value), entry.structure);
    for (let i = 0; i < n; i++) {
      const s = sv[i]!;
      const id = dv[i] === dstNodata ? undefined : structure.get(dv[i]!);
      if (s === srcNodata || !id) continue;
      let m = counts.get(id);
      if (!m) counts.set(id, (m = new Map()));
      m.set(s, (m.get(s) ?? 0) + 1);
      vertices++;
    }
  }
  const keys = [...counts.keys()];
  return {
    pack: { id: manifest.id ?? pack.dir, version: manifest.version ?? '' },
    from,
    to,
    keys,
    parts: keys.map((k) => [...counts.get(k)!].map(([label, v]) => ({ label, vertices: v })).sort((a, b) => a.label - b.label)),
    vertices,
  };
}

/**
 * `[frame][source]` values as `[frame][key]`: each key's mean over its vertices of the value of the
 * source parcel under each one. `column(label)` is the source column a label's value is in, or -1;
 * NaN values (missing, never zero) and unknown labels leave the mean, and a key with none is NaN.
 */
export function remap(values: Float32Array, frames: number, sources: number, cw: Crosswalk, column: (label: number) => number): Float32Array {
  const K = cw.keys.length;
  const out = new Float32Array(frames * K);
  const cols = cw.parts.map((parts) => parts.map((p) => ({ c: column(p.label), w: p.vertices })).filter((p) => p.c >= 0 && p.c < sources));
  for (let f = 0; f < frames; f++) {
    const row = f * sources;
    for (let k = 0; k < K; k++) {
      let sum = 0;
      let weight = 0;
      for (const { c, w } of cols[k]!) {
        const v = values[row + c]!;
        if (Number.isFinite(v)) {
          sum += v * w;
          weight += w;
        }
      }
      out[f * K + k] = weight > 0 ? sum / weight : NaN;
    }
  }
  return out;
}
